import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { payrollInvoiceService } from "./payroll-invoice.services";
import { PAYROLL_INVOICE_KEYS } from "./payroll-invoice.keys";
import { 
  CreatePayrollInvoiceDto, 
  UpdatePayrollInvoiceDto, 
  PayrollQueryDto 
} from "~/types/payroll";

// Get all payroll invoices
export const useGetPayrollInvoices = (query?: PayrollQueryDto) => {
  return useQuery({
    queryKey: PAYROLL_INVOICE_KEYS.lists(),
    queryFn: () => payrollInvoiceService.getAll(query),
  });
};

// Get payroll invoice by ID
export const useGetPayrollInvoice = (id: string) => {
  return useQuery({
    queryKey: PAYROLL_INVOICE_KEYS.detail(id),
    queryFn: () => payrollInvoiceService.getById(id),
    enabled: !!id,
  });
};

// Get payroll dashboard data
export const useGetPayrollDashboard = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: PAYROLL_INVOICE_KEYS.dashboard(),
    queryFn: () => payrollInvoiceService.getDashboardData({startDate, endDate}),
  });
};

// Create payroll invoice mutation
export const useCreatePayrollInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePayrollInvoiceDto) => payrollInvoiceService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.all });
      message.success("Payroll invoice created successfully");
    },
    onError: () => {
      message.error("Failed to create payroll invoice");
    },
  });
};

// Update payroll invoice mutation
export const useUpdatePayrollInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePayrollInvoiceDto }) =>
      payrollInvoiceService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.all });
      message.success("Payroll invoice updated successfully");
    },
    onError: () => {
      message.error("Failed to update payroll invoice");
    },
  });
};

// Delete payroll invoice mutation
export const useDeletePayrollInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => payrollInvoiceService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.all });
      message.success("Payroll invoice deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete payroll invoice");
    },
  });
};

// Mark as paid payroll invoice mutation
export const useMarkPayrollInvoiceAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => payrollInvoiceService.markAsPaid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.all });
      message.success("Payroll invoice marked as paid successfully");
    },
    onError: () => {
      message.error("Failed to mark payroll invoice as paid");
    },
  });
};

// Create payroll invoice with QuickBooks mutation
export const useCreatePayrollInvoiceWithQuickBooks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePayrollInvoiceDto) => payrollInvoiceService.createWithQuickBooks(dto),
    onSuccess: () => {
      // Invalidate all payroll-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: PAYROLL_INVOICE_KEYS.dashboard() });
      // Notification handled in component
    },
    onError: () => {
      // Error notification handled in component
    },
  });
}; 